import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AlertTriangle, Ban, CreditCard, Plane, CheckCircle } from 'lucide-react';
import SearchFormWithQuota from './components/search/SearchFormWithQuota';
import FieldSelector from './components/fields/FieldSelector';
import DataTable from './components/table/DataTable';
import { useFlightSearch } from './hooks/useFlightSearch';
import { useFieldSelection } from './hooks/useFieldSelection';
import { useAuth } from './hooks/useAuth';
import PricingPlans from './components/payment/PricingPlans';
import Login from './components/login/Login';
import Register from './components/login/Register';
import PasswordResetConfirm from './components/login/PasswordResetConfirm';
import ProtectedLayout from './components/layout/ProtectedLayout';
import PaymentPage from './components/payment/PaymentPage';
import SubscriptionList from './components/payment/SubscriptionList';
import LogViewer from './components/logs/LogViewer';
import ReportLogViewer from './components/logs/ReportLogViewer';
import PaymentCancelPage from './components/onetimepayment/PaymentCancelPage';
import OneTimePaymentPage from './components/onetimepayment/OneTimePaymentPage';
import PaymentSuccess from './components/onetimepayment/PaymentSuccess';
import moment from 'moment';
import 'bootstrap/dist/css/bootstrap.min.css';
import FlightStatusComponent from './components/flightstatus/FlightStatusComponent';

// Loading component for initial auth check
const LoadingScreen = () => (
  <div className="min-h-screen bg-gray-100 d-flex align-items-center justify-content-center">
    <div className="text-center">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3 text-muted">Loading...</p>
    </div>
  </div>
);


const Dashboard = ({ user, onLogout, authenticatedFetch, updateUserSubscription }) => {
  const [isDateInvalid, setIsDateInvalid] = useState(false);
  const [clearFiltersSignal, setClearFiltersSignal] = useState(0);
  const [isClientQuotaExceeded, setIsClientQuotaExceeded] = useState(false);
  const [quotaRefreshTrigger, setQuotaRefreshTrigger] = useState(0);
  const [clientQuotaData, setClientQuotaData] = useState(null);
  const [userInfoLoading, setUserInfoLoading] = useState(true);
  const dataTableRef = React.useRef(null);
  
  const [searchParams, setSearchParams] = useState({
    origin: '',
    destination: '',
    opcarrier: '',
    startDate: '',
    endDate: '',
    destinationType: 'airport',
    displayDateFormat: 'Date',
  });

  // Fetch latest user information on mount
  React.useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const response = await authenticatedFetch(`${baseUrl}/api/users/${user.id}`, {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user info');
        }

        const userData = await response.json();
        
        // Update user subscription with latest data
        updateUserSubscription({
          activeSubscription: userData.activeSubscription,
          stripeCustomerId: userData.stripeCustomerId,
          expiryDate: userData.expiryDate,
        });
      } catch (error) {
        console.error('Error fetching user info:', error);
        // Continue with existing user data if fetch fails
      } finally {
        setUserInfoLoading(false);
      }
    };

    // Only fetch if we have a user
    if (user && user.id) {
      fetchUserInfo();
    } else {
      setUserInfoLoading(false);
    }
  }, []); // Empty dependency array - run once on mount

  const {
    flightData,
    loading,
    error,
    hasSearched,
    totalRecords,
    returnedRecords,
    hasMore,
    isLimited,
    limit,
    loadAll,
    downloadUrl,
    fileName,
    currentFilters,
    quotaOver,
    switchedToPermanentQuota,
    usingPermanentQuota,
    previouslyUsingPermanent,
    rowsFromExpiring,
    rowsFromPermanent,
    searchFlights,
    loadAllRecords,
    resetToLimited,
    resetSearch,
    handleServerFilter,
    clearFilters,
    clearFiltersWithSearch
  } = useFlightSearch();

  const {
    selectedFields,
    draftFields,
    filteredFieldsForDisplay,
    handleFieldChange,
    commitSelection
  } = useFieldSelection();

  const handleSearch = async (reset = false) => {
    if (reset) {
      await clearFilters();
      setClearFiltersSignal(prev => prev + 1);
    }
    
    commitSelection();
    const filteredFields = draftFields.filter(
      field => field !== 'o_d_c_name' && field !== 'dupleg_flight'
    );   
    
    await searchFlights(
      user.id, 
      { ...searchParams, fields: filteredFields }, 
      filteredFields, 
      false, 
      {}
    );
    
    setQuotaRefreshTrigger(prev => prev + 1);
  };

  const handleLoadAllRecords = async () => {
    await loadAllRecords();
    setQuotaRefreshTrigger(prev => prev + 1);
  };

  const handleResetToLimited = async () => {
    await resetToLimited();
  };

  const handleServerFilterWithQuota = async (filters) => {
    await handleServerFilter(filters);
    setQuotaRefreshTrigger(prev => prev + 1);
  };

  const handleClearFiltersWithQuota = async () => {
    await clearFilters();
  };

  React.useEffect(() => {
    if (shouldShowResults() && flightData.length > 0 && !loading && dataTableRef.current) {
      setTimeout(() => {
        dataTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [flightData, loading]);

  const handleRenewSubscription = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const endpoint = `${baseUrl}/api/stripe/create-portal-session?customerId=${encodeURIComponent(
        user.stripeCustomerId
      )}`;

      const response = await authenticatedFetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      console.error("Failed to create portal session", err);
    }
  };

  const shouldShowResults = () => {
    return hasSearched && !quotaOver && !isClientQuotaExceeded;
  };

  const handleSearchClick = async () => {
    console.log('Search button clicked');
    
    if (isClientQuotaExceeded) {
      console.log('Search blocked - quota exceeded');
      return;
    }
    
    console.log('Calling handleSearch with reset=true');
    await handleSearch(true);
  };

  const getSearchButtonText = () => {
    if (loading) return 'Searching...';
    if (isClientQuotaExceeded) return 'Quota Exceeded';
    return 'Search Schedules';
  };

  const getSearchButtonClass = () => {
    if (isClientQuotaExceeded) return 'btn btn-danger';
    if (isDateInvalid || loading) return 'btn btn-secondary';
    return 'btn btn-primary';
  };

  // Helper function to get quota display info
  const getQuotaDisplayInfo = () => {
    if (!clientQuotaData || !clientQuotaData.quotas) {
      return {
        consumed: 0,
        total: 1000,
        remaining: 1000
      };
    }

    return {
      consumed: clientQuotaData.consumedRowsSum || 0,
      total: clientQuotaData.totalQuotaSum || 1000,
      remaining: clientQuotaData.remainingQuotaSum || 1000
    };
  };

  const quotaInfo = getQuotaDisplayInfo();

  // Show loading state while fetching user info
  if (userInfoLoading) {
    return (
      <div className="min-h-screen bg-gray-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Redirect to pricing if no active subscription (after loading user info)
  if (!user.activeSubscription) {
    return <Navigate to="/pricing" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Schedule Reporting</h1>
        </div>

        <SearchFormWithQuota
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          setIsDateInvalid={setIsDateInvalid}
          user={user}
          stripeCustomerId={user.stripeCustomerId}
          quotaRefreshTrigger={quotaRefreshTrigger}
          isSearching={loading}
          onQuotaStatusChange={setIsClientQuotaExceeded}
          onQuotaDataChange={setClientQuotaData}
        />

        {!isClientQuotaExceeded && (
          <>
            <FieldSelector
              draftFields={draftFields}
              onFieldChange={handleFieldChange}
            />

            <div className="d-flex justify-content-end" style={{ marginBottom: '1rem' }}>
              <button
                onClick={handleSearchClick}
                disabled={isClientQuotaExceeded || isDateInvalid || loading}
                className={`${getSearchButtonClass()} d-flex align-items-center`}
              >
                {loading ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    {getSearchButtonText()}
                  </>
                ) : (
                  <>
                    <Plane size={16} className="me-2" />
                    {getSearchButtonText()}
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* SUCCESS: Monthly Quota Restored */}
        {previouslyUsingPermanent && !usingPermanentQuota && hasSearched && !isClientQuotaExceeded && !quotaOver && (
          <div className="alert alert-success mb-4" role="alert">
            <div className="d-flex align-items-start">
              <CheckCircle size={20} className="me-2 mt-1 flex-shrink-0" />
              <div className="flex-grow-1">
                <h6 className="alert-heading mb-2">
                  <strong>Monthly Quota Restored!</strong>
                </h6>
                <p className="mb-2">
                  Your monthly subscription quota has been reset. This query used your renewed monthly quota.
                </p>
                <p className="mb-0 small text-muted">
                  Your one-time purchased quota remains available and will only be used after your monthly quota is exhausted.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* INFO: Using Combined Quota or Switched to Permanent */}
        {switchedToPermanentQuota && hasSearched && !isClientQuotaExceeded && !quotaOver && (
          <div className="alert alert-info mb-4" role="alert">
            <div className="d-flex align-items-start">
              <AlertTriangle size={20} className="me-2 mt-1 flex-shrink-0 text-info" />
              <div className="flex-grow-1">
                <h6 className="alert-heading mb-2">
                  <strong>
                    {rowsFromExpiring > 0 && rowsFromPermanent > 0 
                      ? 'Using Combined Quota' 
                      : 'Now Using One-Time Paid Quota'}
                  </strong>
                </h6>
                <p className="mb-2">
                  Your query returned <strong>{totalRecords?.toLocaleString()}</strong> rows. 
                  {rowsFromExpiring > 0 && rowsFromPermanent > 0 ? (
                    <>
                      {' '}We used <strong>{rowsFromExpiring}</strong> rows from your monthly quota 
                      and <strong>{rowsFromPermanent}</strong> rows from your one-time paid quota.
                    </>
                  ) : rowsFromPermanent > 0 ? (
                    <>
                      {' '}Your monthly subscription quota has been exhausted. 
                      We used <strong>{rowsFromPermanent}</strong> rows from your one-time paid quota.
                    </>
                  ) : (
                    <>
                      {' '}We used <strong>{rowsFromExpiring}</strong> rows from your monthly quota.
                    </>
                  )}
                </p>
                <p className="mb-0 small text-muted">
                  {user.expiryDate && (
                    <>Your monthly quota will reset on <strong>{moment(user.expiryDate).format("MM-DD-YYYY hh:mm A")}</strong>. </>
                  )}
                  One-time purchased quota does not expire.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* BADGE: Current quota type - Show after successful search */}
        {hasSearched && !isClientQuotaExceeded && !quotaOver && (
          <div className="mb-3">
            <small className="text-muted">
              Currently using: {usingPermanentQuota ? (
                <span className="badge bg-info">One-Time Paid Quota</span>
              ) : (
                <span className="badge bg-primary">Monthly Subscription Quota</span>
              )}
            </small>
          </div>
        )}

        {/* WARNING: Partial Results - Insufficient Combined Quota */}
        {quotaOver && hasSearched && (
          <div className="alert alert-warning mb-4" role="alert">
            <div className="d-flex align-items-start">
              <AlertTriangle size={20} className="me-2 mt-1 flex-shrink-0" />
              <div className="flex-grow-1">
                <h6 className="alert-heading mb-2">
                  <strong>Partial Results - Quota Limit Reached</strong>
                </h6>
                <p className="mb-3">
                  Your query returned <strong>{totalRecords?.toLocaleString()}</strong> rows, but you only had <strong>{returnedRecords?.toLocaleString()}</strong> rows of quota remaining.
                  <br />
                  <span className="text-muted">
                    Showing <strong>{returnedRecords?.toLocaleString()}</strong> of <strong>{totalRecords?.toLocaleString()}</strong> total results.
                    {rowsFromExpiring > 0 && rowsFromPermanent > 0 && (
                      <> Used <strong>{rowsFromExpiring}</strong> from monthly + <strong>{rowsFromPermanent}</strong> from one-time paid.</>
                    )}
                  </span>
                </p>
                <p className="mb-3">
                  {user.expiryDate && (
                    <>Your monthly quota will reset on <strong>{moment(user.expiryDate).format("MM-DD-YYYY hh:mm A")}</strong>. </>
                  )}
                  To see all results now, you can purchase additional one-time quota.
                </p>

                <div className="d-flex gap-2 flex-wrap">
                  <button 
                    className="btn btn-primary btn-sm d-flex align-items-center"
                    onClick={() => window.location.href = '/one-time-payment'}
                    title="Purchase additional one-time quota"
                  >
                    <CreditCard size={16} className="me-2" />
                    Buy Additional Quota
                  </button>
                  <button 
                    className="btn btn-outline-dark btn-sm d-flex align-items-center"
                    onClick={handleRenewSubscription}
                    title="View pricing plans and upgrade your subscription"
                  >
                    <CreditCard size={16} className="me-2" />
                    Upgrade Subscription
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* DANGER: All Quota Exhausted (before search) */}
        {isClientQuotaExceeded && clientQuotaData && !quotaOver && !hasSearched && (
          <div className="alert alert-danger mb-4" role="alert">
            <div className="d-flex align-items-start">
              <Ban size={20} className="me-2 mt-1 flex-shrink-0" />
              <div className="flex-grow-1">
                <h6 className="alert-heading mb-2">
                  <strong>Quota Exhausted!</strong>
                </h6>
                <p className="mb-3">
                  All your quota has been exhausted. You have used <strong>{quotaInfo.consumed.toLocaleString()}</strong> of <strong>{quotaInfo.total.toLocaleString()}</strong> available rows. 
                  {user.expiryDate && (
                    <> Your monthly quota will reset on <strong>{moment(user.expiryDate).format("MM-DD-YYYY hh:mm A")}</strong>.</>
                  )}
                </p>
                <div className="d-flex gap-2 flex-wrap">
                  <button 
                    className="btn btn-primary btn-sm d-flex align-items-center"
                    onClick={() => window.location.href = '/one-time-payment'}
                    title="Purchase additional one-time quota"
                  >
                    <CreditCard size={16} className="me-2" />
                    Buy Additional Quota
                  </button>
                  <button 
                    className="btn btn-outline-dark btn-sm d-flex align-items-center"
                    onClick={handleRenewSubscription}
                    title="View pricing plans and upgrade your subscription"
                  >
                    <CreditCard size={16} className="me-2" />
                    Manage Subscription
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        

        {/* Data Table - Show results */}
        {shouldShowResults() && (
          <div ref={dataTableRef}>
            <DataTable
              data={flightData}
              selectedFields={filteredFieldsForDisplay}
              loading={loading}
              totalRecords={totalRecords}
              returnedRecords={returnedRecords}
              hasMore={hasMore}
              isLimited={isLimited}
              limit={limit}
              loadAll={loadAll}
              onLoadAll={handleLoadAllRecords}
              onResetToLimited={handleResetToLimited}
              onServerFilter={handleServerFilterWithQuota}
              onClearFilters={clearFiltersWithSearch}
              downloadUrl={downloadUrl}
              fileName={fileName}
              userId={user.id}
              clearFiltersSignal={clearFiltersSignal}
            />
          </div>
        )}

        {/* WARNING: Quota exceeded before search attempt */}
        {quotaOver && !hasSearched && (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <AlertTriangle size={20} className="me-2" />
            <div>
              <strong>Quota Exceeded</strong>
              <br />
              You have reached your quota limit. New searches are disabled until your quota resets or you purchase additional quota.
            </div>
          </div>
        )}

        {/* Initial state - No search performed yet */}
        {!hasSearched && !quotaOver && !loading && !isClientQuotaExceeded && (
          <div className="text-center py-5">
            <p className="text-muted">Enter your search criteria above and click "Search" to view flight schedules.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, user, loading }) => {
  if (loading) {
    return <LoadingScreen />;
  }
  return user ? children : <Navigate to="/login" replace />;
};

// Main App Component with Routes
const App = () => {
  const { 
    user, 
    isAuthenticated, 
    loading, 
    login, 
    logout, 
    authenticatedFetch,
    updateUserSubscription,
    refreshing
  } = useAuth();

  // Show loading screen while checking authentication
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Router basename={process.env.REACT_APP_BASE_URL}>
      <Routes>
        {/* Public Routes - Login and Password Reset */}
        <Route 
          path="/login" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Login onLogin={login} />
            )
          } 
        />

        <Route 
          path="/register" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Register onRegister={login} />
            )
          } 
        />
        
        <Route 
          path="/reset-password" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <PasswordResetConfirm />
            )
          } 
        />
        
        {/* Protected Routes */}
        <Route 
          path="/pricing" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <PricingPlans 
                  user={user}
                  updateUserSubscription={updateUserSubscription}
                />
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <Dashboard 
                  user={user} 
                  onLogout={logout}
                  authenticatedFetch={authenticatedFetch}
                  updateUserSubscription={updateUserSubscription}
                />
              </ProtectedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/payment" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <PaymentPage 
                  user={user}
                  authenticatedFetch={authenticatedFetch}
                  updateUserSubscription={updateUserSubscription}
                />
              </ProtectedLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/subscriptions" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <SubscriptionList 
                  user={user}
                  authenticatedFetch={authenticatedFetch}
                  updateUserSubscription={updateUserSubscription}
                />
              </ProtectedLayout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/logs" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <LogViewer authenticatedFetch={authenticatedFetch} />
              </ProtectedLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/one-time-payment" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <OneTimePaymentPage user={user} />
              </ProtectedLayout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/payment-success" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <PaymentSuccess user={user} />
              </ProtectedLayout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/payment-cancel" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <PaymentCancelPage />
              </ProtectedLayout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/report-logs" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <ReportLogViewer 
                  authenticatedFetch={authenticatedFetch} 
                  user={user}
                />
              </ProtectedLayout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/flightstatus" 
          element={
            <ProtectedRoute user={user} loading={loading}>
              <ProtectedLayout user={user} onLogout={logout}>
                <FlightStatusComponent user={user} />
              </ProtectedLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* Default Routes */}
        <Route 
          path="/" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        
        <Route 
          path="*" 
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
      </Routes>
    </Router>
  );
};

export default App;